import React from 'react';
import PropTypes from 'prop-types';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Dialog, DialogType, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import clsx from 'clsx';
import { Form, Field, Formik } from 'formik';
import * as Yup from 'yup';
import SavingIndicator from '../SavingIndicator';
import RemovingIndicator from '../RemovingIndicator';
import styles from './ConfirmationDialog.style';
import TextField from '../TextField';

const rejectionValidationSchema = Yup.object().shape({
  comment: Yup.string()
    .required('Required')
    .test('empty-check', 'Comment cannot be empty', reason => {
      if (!reason || reason.trim().length === 0) {
        return false;
      }
      return true;
    }),
});

const ConfirmationDialog = ({
  onDismiss,
  onSubmit,
  isLoading,
  className,
  dialogContentProps,
  dialogBodyContent,
  isRemoving,
  isRejectClick,
  ...other
}) => {
  const initial = {
    comment: '',
  };

  const submitHandler = async values => {
    const formValues = {
      comment: values.comment,
    };
    await onSubmit(formValues);
  };

  const validationSchema = isRejectClick ? rejectionValidationSchema : null;

  return (
    <Dialog
      dialogContentProps={dialogContentProps}
      modalProps={{
        className: clsx(styles.root, className),
      }}
      onDismiss={onDismiss}
      onSubmit={onSubmit}
      {...other}
    >
      <Formik
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={validationSchema}
        onSubmit={submitHandler}
        initialValues={initial}
      >
        {({ isSubmitting }) => {
          return (
            <Form>
              <p className={styles.modalText}>{dialogBodyContent?.content}</p>
              {isRejectClick && (
                <div className={styles.dialogContent}>
                  <Field
                    id="comment"
                    name="comment"
                    label="Comment"
                    component={TextField}
                    required
                    multiline
                  />
                </div>
              )}
              <DialogFooter>
                <DefaultButton onClick={onDismiss} disabled={isSubmitting || isRemoving}>
                  Cancel
                </DefaultButton>
                <PrimaryButton type="submit" disabled={isSubmitting || isRemoving}>
                  {dialogBodyContent?.buttonText}
                </PrimaryButton>
                {isSubmitting && <SavingIndicator className={styles.saving} />}
                {isRemoving && <RemovingIndicator />}
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

ConfirmationDialog.propTypes = {
  onDismiss: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isRemoving: PropTypes.bool,
  className: PropTypes.string,
  dialogContentProps: PropTypes.shape({
    type: PropTypes.number,
    title: PropTypes.string,
    closeButtonAriaLabel: PropTypes.string,
  }),
  dialogBodyContent: PropTypes.shape({
    content: PropTypes.string,
    buttonText: PropTypes.string,
  }),
  isRejectClick: PropTypes.bool,
};

ConfirmationDialog.defaultProps = {
  isLoading: false,
  isRemoving: false,
  className: 'normal',
  dialogContentProps: {
    type: DialogType.close,
    title: '',
    closeButtonAriaLabel: 'Close',
  },
  dialogBodyContent: {
    content: '',
    buttonText: 'Close',
  },
  isRejectClick: false,
};

export default ConfirmationDialog;
